import React from 'react';
import { Box, Flex } from '@chakra-ui/react';
import {
  Button,
  useToast,
  Input,
  TextArea,
  FormFeedback
} from '@morressier/alembic';
import { FormProvider, useForm } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import {
  ICreateJournalPayload,
  useCreateJournalMutation
} from 'src/api/mutations';
import LogoUrlFileUpload from './[id]/Configure/JournalConfig/LogoUrlFileUpload';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';

interface JournalFormData {
  name: string;
  shortname: string;
  description: string;
  introduction: string;
  issn_online: string;
  issn_print: string;
  url: string;
  logoUrl: string;
  logoFile: any;
}

interface CreateJournalFormProps {
  workspaceId: string;
  organizationId: string;
  onSuccess: () => void;
  onClose: () => void;
}

const getEmptyFormValues = (): JournalFormData => ({
  name: '',
  shortname: '',
  description: '',
  introduction: '',
  issn_online: '',
  issn_print: '',
  url: '',
  logoUrl: '',
  logoFile: null
});

const buildJournalPayload = (
  formValues: JournalFormData,
  workspaceId: string,
  organizationId: string
): ICreateJournalPayload => ({
  type: 'journal',
  organizationId,
  workspaceId,
  data: {
    name: formValues.name,
    shortname: formValues.shortname,
    description: formValues.description,
    introduction: formValues.introduction
  },
  config: {
    issn: {
      online: formValues.issn_online,
      print: formValues.issn_print
    },
    url: formValues.url,
    logoUrl: formValues.logoUrl
  }
});

interface FormFieldProps {
  register: any;
  setValue: any;
  watch: any;
  errors: any;
  clearErrors: any;
}

const JournalNameField = ({
  register,
  setValue,
  watch,
  errors,
  clearErrors
}: FormFieldProps) => {
  const [tempValue, setTempValue] = React.useState<string>(watch('name') || '');

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <FieldLabel
        label="Journal Name"
        subText=""
        isRequired={true}
        helpText=""
      />
      <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={2}>
        <Input
          id="name"
          value={tempValue}
          {...register('name', { required: 'Journal name is required' })}
          isInvalid={!!errors.name}
          placeholder="e.g., Journal of Computer Science"
          onChange={e => {
            setTempValue(e.target.value);
            if (errors.name) clearErrors('name');
          }}
          onBlur={() => {
            setValue('name', tempValue.trim());
          }}
        />
        {errors.name && (
          <FormFeedback variant="error">{errors.name.message}</FormFeedback>
        )}
      </Flex>
    </Flex>
  );
};

const ShortNameField = ({
  register,
  setValue,
  watch,
  errors,
  clearErrors
}: FormFieldProps) => {
  const [tempValue, setTempValue] = React.useState<string>(
    watch('shortname') || ''
  );

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <FieldLabel
        label="Journal Code"
        subText=""
        isRequired={true}
        helpText=""
      />
      <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={2}>
        <Input
          id="shortname"
          value={tempValue}
          {...register('shortname', {
            required: 'Journal code is required',
            pattern: {
              value: /^[A-Za-z0-9]+$/,
              message: 'Only letters and numbers are allowed'
            }
          })}
          isInvalid={!!errors.shortname}
          placeholder="e.g., JCS123"
          maxLength={6}
          onChange={e => {
            setTempValue(e.target.value);
            if (errors.shortname) clearErrors('shortname');
          }}
          onBlur={() => {
            setValue('shortname', tempValue.trim(), {
              shouldValidate: true
            });
          }}
        />
        {errors.shortname && (
          <FormFeedback variant="error">
            {errors.shortname.message}
          </FormFeedback>
        )}
      </Flex>
    </Flex>
  );
};

const DescriptionField = ({
  register,
  setValue,
  watch,
  errors,
  clearErrors
}: FormFieldProps) => {
  const [tempValue, setTempValue] = React.useState<string>(
    watch('description') || ''
  );

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <FieldLabel
        label="Description"
        subText=""
        isRequired={true}
        helpText=""
      />
      <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={2}>
        <TextArea
          id="description"
          value={tempValue}
          {...register('description', { required: 'Description is required' })}
          isInvalid={!!errors.description}
          placeholder="Submission context for the Journal..."
          rows={3}
          onChange={e => {
            setTempValue(e.target.value);
            if (errors.description) clearErrors('description');
          }}
          onBlur={() => {
            setValue('description', tempValue.trim());
          }}
        />
        {errors.description && (
          <FormFeedback variant="error">
            {errors.description.message}
          </FormFeedback>
        )}
      </Flex>
    </Flex>
  );
};

const IntroductionField = ({
  register,
  setValue,
  watch,
  errors,
  clearErrors
}: FormFieldProps) => {
  const [tempValue, setTempValue] = React.useState<string>(
    watch('introduction') || ''
  );

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <FieldLabel
        label="Introduction"
        subText=""
        isRequired={true}
        helpText=""
      />
      <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={2}>
        <TextArea
          id="introduction"
          value={tempValue}
          {...register('introduction', {
            required: 'Introduction is required'
          })}
          isInvalid={!!errors.introduction}
          placeholder="Welcome message for the submission portal..."
          rows={3}
          onChange={e => {
            setTempValue(e.target.value);
            if (errors.introduction) clearErrors('introduction');
          }}
          onBlur={() => {
            setValue('introduction', tempValue.trim());
          }}
        />
        {errors.introduction && (
          <FormFeedback variant="error">
            {errors.introduction.message}
          </FormFeedback>
        )}
      </Flex>
    </Flex>
  );
};

const IssnOnlineField = ({
  register,
  setValue,
  watch,
  errors,
  clearErrors
}: FormFieldProps) => {
  const [tempValue, setTempValue] = React.useState<string>(
    watch('issn_online') || ''
  );

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <FieldLabel
        label="ISSN (Online)"
        subText=""
        isRequired={false}
        helpText=""
      />
      <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={2}>
        <Input
          id="issn_online"
          value={tempValue}
          {...register('issn_online', {
            pattern: {
              value: /^[0-9]{4}-[0-9]{3}[0-9Xx]$/,
              message: 'Enter a valid ISSN in the format 1234-5678'
            }
          })}
          isInvalid={!!errors.issn_online}
          placeholder="e.g., 1234-5678"
          onChange={e => {
            setTempValue(e.target.value);
            if (errors.issn_online) clearErrors('issn_online');
          }}
          onBlur={() => {
            setValue('issn_online', tempValue.trim());
          }}
        />
        {errors.issn_online && (
          <FormFeedback variant="error">
            {errors.issn_online.message}
          </FormFeedback>
        )}
      </Flex>
    </Flex>
  );
};

const IssnPrintField = ({
  register,
  setValue,
  watch,
  errors,
  clearErrors
}: FormFieldProps) => {
  const [tempValue, setTempValue] = React.useState<string>(
    watch('issn_print') || ''
  );

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <FieldLabel
        label="ISSN (Print)"
        subText=""
        isRequired={false}
        helpText=""
      />
      <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={2}>
        <Input
          id="issn_print"
          value={tempValue}
          {...register('issn_print', {
            pattern: {
              value: /^[0-9]{4}-[0-9]{3}[0-9Xx]$/,
              message: 'Enter a valid ISSN in the format 1234-5678'
            }
          })}
          isInvalid={!!errors.issn_print}
          placeholder="e.g., 8765-4321"
          onChange={e => {
            setTempValue(e.target.value);
            if (errors.issn_print) clearErrors('issn_print');
          }}
          onBlur={() => {
            setValue('issn_print', tempValue.trim());
          }}
        />
        {errors.issn_print && (
          <FormFeedback variant="error">
            {errors.issn_print.message}
          </FormFeedback>
        )}
      </Flex>
    </Flex>
  );
};

const JournalUrlField = ({
  register,
  setValue,
  watch,
  errors,
  clearErrors
}: FormFieldProps) => {
  const [tempValue, setTempValue] = React.useState<string>(watch('url') || '');

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <FieldLabel
        label="Journal URL"
        subText=""
        isRequired={true}
        helpText=""
      />
      <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={2}>
        <Input
          id="url"
          value={tempValue}
          {...register('url', {
            required: 'Journal URL is required',
            pattern: {
              value: /^https?:\/\/.+/,
              message: 'Please enter a valid URL'
            }
          })}
          isInvalid={!!errors.url}
          placeholder="https://journal.example.com"
          type="url"
          onChange={e => {
            setTempValue(e.target.value);
            if (errors.url) clearErrors('url');
          }}
          onBlur={() => {
            setValue('url', tempValue.trim());
          }}
        />
        {errors.url && (
          <FormFeedback variant="error">{errors.url.message}</FormFeedback>
        )}
      </Flex>
    </Flex>
  );
};

const CreateJournalForm = ({
  workspaceId,
  organizationId,
  onSuccess,
  onClose
}: CreateJournalFormProps) => {
  const toast = useToast();
  const methods = useForm<JournalFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: getEmptyFormValues()
  });

  const {
    handleSubmit,
    formState,
    getValues,
    register,
    setValue,
    watch,
    clearErrors,
    setError
  } = methods;
  const { errors } = formState;
  const createJournalMutation = useCreateJournalMutation();

  const uploadingLogoFile = watch('logoFile');

  const handleCreateJournal = async (formValues: JournalFormData) => {
    const issnOnline = formValues.issn_online?.trim().toLowerCase();
    const issnPrint = formValues.issn_print?.trim().toLowerCase();

    if (issnOnline && issnPrint && issnOnline === issnPrint) {
      setError('issn_print', {
        type: 'manual',
        message: 'ISSN online and print cannot be the same'
      });
      scrollToFirstError();
      return;
    }

    formValues.logoUrl = getValues('logoFile')?.url;

    const payload = buildJournalPayload(
      formValues,
      workspaceId,
      organizationId
    );

    createJournalMutation.mutate(payload, {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: 'Success',
          description: 'Journal created successfully',
          isClosable: true
        });
        onSuccess();
        onClose();
        methods.reset();
      },
      onError: (error: any) => {
        const message =
          typeof error?.response?.data === 'string'
            ? error.response.data
            : error?.message || 'Failed to create journal';

        toast({
          variant: 'error',
          title: 'Error',
          description: message,
          isClosable: true
        });
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <Box
        as="form"
        onSubmit={handleSubmit(handleCreateJournal, scrollToFirstError)}
      >
        <Box display="grid" gap={4}>
          {/* Row 1: Journal Name | Short Name */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
            <JournalNameField
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
              clearErrors={clearErrors}
            />

            <ShortNameField
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
              clearErrors={clearErrors}
            />
          </Box>

          {/* Row 2: Description */}
          <DescriptionField
            register={register}
            setValue={setValue}
            watch={watch}
            errors={errors}
            clearErrors={clearErrors}
          />

          {/* Row 3: Introduction */}
          <IntroductionField
            register={register}
            setValue={setValue}
            watch={watch}
            errors={errors}
            clearErrors={clearErrors}
          />

          {/* Config Section Divider */}
          <Box borderTop="1px solid #e2e8f0" pt={4} />

          {/* Row 4: ISSN (Online) | ISSN (Print) */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
            <IssnOnlineField
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
              clearErrors={clearErrors}
            />

            <IssnPrintField
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
              clearErrors={clearErrors}
            />
          </Box>

          {/* Row 5: Journal URL */}
          <JournalUrlField
            register={register}
            setValue={setValue}
            watch={watch}
            errors={errors}
            clearErrors={clearErrors}
          />

          {/* Row 6: Logo Upload */}
          <LogoUrlFileUpload
            organizationId={organizationId}
            field={'config.logoUrl'}
            tempField="logoFile"
            label="Journal Logo"
            isRequired={true}
          />

          {/* Action Buttons */}
          <Box display="flex" gap={2} justifyContent="flex-end" mt={6}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                uploadingLogoFile?.isUploading ||
                createJournalMutation?.isPending
              }
              isLoading={createJournalMutation.isPending}
            >
              Create Journal
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default CreateJournalForm;
